const { ODDS_THRESHOLD } = require('../../constants');
const { BasketModes, StatusStates } = require('../../types');

const getDecimal = (value) => (value.includes('.') ? parseInt(value.split('.')[1], 10) : 0);

const not = (f) => (basket, item, entities) => !f(basket, item, entities);
const isCombined = (basket) => basket?.mode === BasketModes.COMBINED;
const isSimple = (basket) => basket?.mode === BasketModes.SIMPLE;
const isSystem = (basket) => basket?.mode === BasketModes.SYSTEM;
const hasMyMatch = (_, item) => !!item?.betbuilder;
const isBoostedOdd = (sportId) => sportId === 100000;

const hasFreebet = (useFreebet) => (basket, item) =>
  basket?.mode === BasketModes.SIMPLE
    ? !!item?.selectedFreebetId === useFreebet
    : !!basket?.selectedFreebetId === useFreebet;

const hasOutright = (useOutright) => (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? !!entities?.matches[item?.matchId]?.isOutright === useOutright
    : Object.values(basket?.items).some(
        (itm) => !!entities?.matches[itm?.matchId]?.isOutright === useOutright
      );

const hasBoostedOdds = (useBoostedOdd) => (basket, item) =>
  basket?.mode === BasketModes.SIMPLE
    ? isBoostedOdd(item?.sportId) === useBoostedOdd
    : Object.values(basket?.items).some((itm) => isBoostedOdd(itm?.sportId) === useBoostedOdd);

const hasNoOutrightButBoostedOdds = (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? hasOutright(false)(basket, item, entities) || isBoostedOdd(item?.sportId)
    : Object.values(basket?.items).some(
        (itm) => itm?.sportId === 100000 || !entities?.matches[itm?.matchId]?.isOutright
      );

const isLive = (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? entities?.matches[item?.matchId]?.status === StatusStates.LIVE
    : Object.values(basket?.items).every(
        (itm) => entities?.matches[itm.matchId]?.status === StatusStates.LIVE
      );

const isPrematch = (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? entities?.matches[item?.matchId]?.status === StatusStates.PREMATCH
    : Object.values(basket?.items).every(
        (itm) => entities?.matches[itm.matchId]?.status === StatusStates.PREMATCH
      );

const isOddGreaterOrEqualThan = (odds) => (basket, item) =>
  basket?.mode === BasketModes.SIMPLE
    ? item?.value > odds - ODDS_THRESHOLD
    : Object.values(basket?.items).reduce((val, itm) => val * itm.value, 1) > odds - ODDS_THRESHOLD;

const isSportIdValid = (sportIds) => (basket, item) =>
  basket?.mode === BasketModes.SIMPLE
    ? sportIds.includes(item?.sportId)
    : Object.values(basket?.items).every((itm) => sportIds.includes(itm.sportId));

const isMatchIdValid = (matchIds) => (basket, item) =>
  basket?.mode === BasketModes.SIMPLE
    ? matchIds.includes(item?.matchId)
    : Object.values(basket?.items).every((itm) => matchIds.includes(itm.matchId));

const isCategoryIdValid = (categoryIds) => (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? categoryIds.includes(entities?.matches[item?.matchId]?.categoryId)
    : Object.values(basket?.items).every((itm) =>
        categoryIds.includes(entities?.matches[itm.matchId]?.categoryId)
      );

const isTournamentIdValid = (tournamentIds) => (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? tournamentIds.includes(entities?.matches[item?.matchId]?.tournamentId)
    : Object.values(basket?.items).every((itm) =>
        tournamentIds.includes(entities?.matches[itm.matchId]?.tournamentId)
      );

const isBetTypeIdValid = (betIds) => (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? betIds.includes(entities?.bets[item?.betId]?.betType)
    : Object.values(basket?.items).every((itm) =>
        betIds.includes(entities?.bets[itm.betId]?.betType)
      );

const hasNoSportIdsIn = (sportIds) => (basket, item) =>
  basket?.mode === BasketModes.SIMPLE
    ? !sportIds.includes(item?.sportId)
    : Object.values(basket?.items).some((itm) => !sportIds.includes(itm.sportId));

const hasNoMatchIdsIn = (matchIds) => (basket, item) =>
  basket?.mode === BasketModes.SIMPLE
    ? !matchIds.includes(item?.matchId)
    : Object.values(basket?.items).some((itm) => !matchIds.includes(itm.matchId));

const hasNoCategoryIdsIn = (categoryIds) => (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? !categoryIds.includes(entities?.matches[item?.matchId]?.categoryId)
    : Object.values(basket?.items).some(
        (itm) => !categoryIds.includes(entities?.matches[itm.matchId]?.categoryId)
      );

const hasNoTournamentIdsIn = (tournamentIds) => (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? !tournamentIds.includes(entities?.matches[item?.matchId]?.tournamentId)
    : Object.values(basket?.items).some(
        (itm) => !tournamentIds.includes(entities?.matches[itm.matchId]?.tournamentId)
      );

const hasNoBetTypesIn = (betIds) => (basket, item, entities) =>
  basket?.mode === BasketModes.SIMPLE
    ? !betIds.includes(entities?.bets[item?.betId]?.betType)
    : Object.values(basket?.items).some(
        (itm) => !betIds.includes(entities?.bets[itm.betId]?.betType)
      );

const isBetAmountGreaterOrEqualThan = (betAmount) => (basket, item) =>
  basket?.mode === BasketModes.COMBINED
    ? parseInt(basket.stake, 10) >= betAmount
    : parseInt(item?.stake, 10) >= betAmount;

const isMinOddInCombinedValid = (odds) => (basket) =>
  Object.values(basket?.items).every(({ value }) => value >= odds);

const isMinCombinedBetsValid = (nbBets) => (basket) =>
  Object.values(basket?.items).length >= nbBets;

const isMinMyMatchLineValid =
  (nbLines) =>
  (_, { selectionItems }) =>
    Object.values(selectionItems).length >= nbLines;

const isBetCentsValid = (value) => (basket, item) =>
  basket?.mode === BasketModes.COMBINED
    ? getDecimal(basket?.stake) === value
    : getDecimal(item?.stake) === value;

const isStartingBeforeMaxStartDate = (maxStartDate) => (basket, item, entities) => {
  if (basket?.mode === BasketModes.SIMPLE) {
    if (entities?.matches[item?.matchId]?.matchStart) {
      return entities.matches[item.matchId].matchStart * 1000 <= maxStartDate;
    }
  } else if (basket?.items) {
    return Object.values(basket?.items).every((itm) => {
      if (entities?.matches[itm.matchId]?.matchStart) {
        return entities.matches[itm.matchId].matchStart * 1000 <= maxStartDate;
      }
      return false;
    });
  }
  return false;
};

const isStartingAfterMinStartDate = (minStartDate) => (basket, item, entities) => {
  if (basket?.mode === BasketModes.SIMPLE) {
    if (entities?.matches[item?.matchId]?.matchStart) {
      return entities.matches[item.matchId].matchStart * 1000 >= minStartDate;
    }
  } else if (basket?.items) {
    return Object.values(basket?.items).every((itm) => {
      if (entities?.matches[itm.matchId]?.matchStart) {
        return entities.matches[itm.matchId].matchStart * 1000 >= minStartDate;
      }
      return false;
    });
  }
  return false;
};

const isSimpleAndNotMyMatch = (basket, item) => isSimple(basket) && !hasMyMatch(basket, item);

module.exports = {
  not,
  isLive,
  isPrematch,
  isCombined,
  isSimple,
  isSystem,
  hasFreebet,
  hasOutright,
  hasBoostedOdds,
  hasNoOutrightButBoostedOdds,
  hasNoSportIdsIn,
  hasNoMatchIdsIn,
  hasNoCategoryIdsIn,
  hasNoTournamentIdsIn,
  hasNoBetTypesIn,
  isSimpleAndNotMyMatch,
  hasMyMatch,
  isBetCentsValid,
  isSportIdValid,
  isMatchIdValid,
  isCategoryIdValid,
  isTournamentIdValid,
  isBetTypeIdValid,
  isOddGreaterOrEqualThan,
  isBetAmountGreaterOrEqualThan,
  isMinOddInCombinedValid,
  isMinCombinedBetsValid,
  isMinMyMatchLineValid,
  isStartingBeforeMaxStartDate,
  isStartingAfterMinStartDate,
};
